import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import {
  getApiSampleTestsOptions,
  getApiShipmentsOptions,
  getApiShipmentsTaskSummaryOptions,
  type GetApiSampleTestsError,
  type GetApiShipmentsError,
  type GetApiShipmentsTaskSummaryError,
  type PageOfSampleTestDto,
  type PageOfShipmentDto,
  type ShipmentTaskSummaryDto,
} from '@/api';

import { ACTIVE_SHIPMENT_STATUSES } from '../../../lib/shipmentStatus';
import { toTasksQuery, type ResolvedTasksSearch } from '../lib/searchParams';

/** Сколько задержанных показать на «Моих задачах»; остальные - в списке по ссылке. */
export const DELAYED_LIMIT = 10;

/** Сколько задач «Узнать результат теста» показать; остальные - в плане тестирования по ссылке. */
export const DUE_SAMPLE_TESTS_LIMIT = 5;

/*
 * Типы возврата заданы явно (TS2742). Ключи - кодогенератора, общие со списками
 * отгрузок: запись, сбрасывающая списки, обновляет и задачи, и сводку.
 */

/** Сводка: без клиента - показатели и выбор клиента, с клиентом - счётчики чипов. */
export const useTaskSummary = (
  partnerId: string | undefined,
): UseQueryResult<ShipmentTaskSummaryDto, GetApiShipmentsTaskSummaryError> =>
  useQuery({
    ...getApiShipmentsTaskSummaryOptions({ query: { partnerId } }),
    placeholderData: (previous) => previous,
  });

/** Страница задач по клиенту, чипу и странице из адресной строки. */
export const useTasks = (
  resolved: ResolvedTasksSearch,
): UseQueryResult<PageOfShipmentDto, GetApiShipmentsError> =>
  useQuery({
    ...getApiShipmentsOptions({ query: toTasksQuery(resolved) }),
    placeholderData: (previous) => previous,
  });

/** Задержанные среди активных - самые ранние по плановой дате сверху. */
export const useDelayedShipments = (): UseQueryResult<
  PageOfShipmentDto,
  GetApiShipmentsError
> =>
  useQuery(
    getApiShipmentsOptions({
      query: {
        Statuses: [...ACTIVE_SHIPMENT_STATUSES],
        IsDelayed: true,
        Sort: 'plannedDate:asc',
        PageSize: DELAYED_LIMIT,
      },
    }),
  );

/*
 * «Узнать результат теста» - тесты образцов, которым пора: идут, дата теста
 * сегодня или прошла (правило - backend, грань isDue). Ближайшие по дате теста
 * сверху; totalRecords - счётчик показателя и блока. Ключ кодогенератора общий с
 * планом тестирования: запись теста сбрасывает и его.
 */
export const useDueSampleTests = (): UseQueryResult<
  PageOfSampleTestDto,
  GetApiSampleTestsError
> =>
  useQuery(
    getApiSampleTestsOptions({
      query: {
        IsDue: true,
        Sort: 'testDate:asc',
        PageSize: DUE_SAMPLE_TESTS_LIMIT,
      },
    }),
  );
